import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Car, Plus, Pencil, Trash2 } from "lucide-react";
import { VehicleDialog } from "./VehicleDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const VehiclesSection = ({ vehicles, onChanged }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const openAdd = () => { setEditing(null); setDialogOpen(true); };
  const openEdit = (v) => { setEditing(v); setDialogOpen(true); };

  const save = async (form) => {
    setSaving(true);
    try {
      if (editing?.vehicle_id) {
        await axios.patch(`${API}/vehicles/${editing.vehicle_id}`, form, { withCredentials: true });
        toast.success("Vehicle updated");
      } else {
        await axios.post(`${API}/vehicles`, form, { withCredentials: true });
        toast.success("Vehicle added");
      }
      setDialogOpen(false);
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save vehicle");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (v) => {
    if (!window.confirm(`Remove ${v.year} ${v.make} ${v.model} from your garage?`)) return;
    try {
      await axios.delete(`${API}/vehicles/${v.vehicle_id}`, { withCredentials: true });
      toast.success("Vehicle removed");
      onChanged();
    } catch {
      toast.error("Could not remove vehicle");
    }
  };

  return (
    <div className="bg-[#121212] border border-zinc-800 rounded-xl p-8" data-testid="portal-vehicles-section">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Car className="h-5 w-5 text-blue-500" />
          <h3 className="font-heading text-lg font-semibold text-white">My Garage</h3>
        </div>
        <button
          data-testid="add-vehicle-button"
          onClick={openAdd}
          className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white rounded-sm px-4 py-2 text-sm font-semibold transition-colors"
        >
          <Plus className="h-4 w-4" /> Add Vehicle
        </button>
      </div>

      {vehicles.length === 0 ? (
        <p className="text-zinc-500 text-sm" data-testid="vehicles-empty-state">
          No vehicles yet. Add your vehicle so we can track its service history and remind you when maintenance is due.
        </p>
      ) : (
        <div className="space-y-3">
          {vehicles.map((v) => (
            <div
              key={v.vehicle_id}
              data-testid={`vehicle-row-${v.vehicle_id}`}
              className="border border-zinc-800 rounded-sm p-4 flex flex-wrap items-center justify-between gap-3"
            >
              <div>
                <div className="text-white font-semibold">
                  {v.nickname ? `${v.nickname} — ` : ""}{v.year} {v.make} {v.model}{v.trim ? ` ${v.trim}` : ""}
                </div>
                <div className="text-zinc-500 text-xs mt-0.5">
                  {[v.color, v.license_plate && `Plate: ${v.license_plate}`, v.mileage && `${v.mileage} mi`]
                    .filter(Boolean).join(" · ") || "Tap edit to add details"}
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  data-testid={`edit-vehicle-${v.vehicle_id}`}
                  onClick={() => openEdit(v)}
                  className="p-2 border border-zinc-800 rounded-sm text-zinc-400 hover:text-white transition-colors"
                  aria-label="Edit vehicle"
                >
                  <Pencil className="h-4 w-4" />
                </button>
                <button
                  data-testid={`delete-vehicle-${v.vehicle_id}`}
                  onClick={() => remove(v)}
                  className="p-2 border border-zinc-800 rounded-sm text-zinc-400 hover:text-red-400 transition-colors"
                  aria-label="Delete vehicle"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {dialogOpen && (
        <VehicleDialog
          key={editing?.vehicle_id || "new"}
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          vehicle={editing}
          onSave={save}
          saving={saving}
        />
      )}
    </div>
  );
};
